import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  GetAllInOrdersParams,
  InOrdersDataSourceService
} from '../data-sources/v3/in-orders/in-orders-data-source.service';
import { InOrder } from 'src/models/InOrder';

const PAGE_SIZE_OPTIONS = [30, 60, 90];

export type InOrdersSortParams = Pick<GetAllInOrdersParams, 'orderBy' | 'sortBy'>;
export type InOrdersFilterParams = Omit<GetAllInOrdersParams, 'limit' | 'offset' | 'orderBy' | 'sortBy'>;

interface InOrdersApiResponse {
  count: number;
  values: InOrder[];
}

@Injectable({
  providedIn: 'root'
})
export class InOrdersService {
  private $pageSize = new BehaviorSubject<number>(PAGE_SIZE_OPTIONS[0]);
  private $totalItems = new BehaviorSubject<number>(0);
  private $pageCount = new BehaviorSubject<number>(0);
  private $atLastPage = new BehaviorSubject<boolean>(false);
  private $atFirstPage = new BehaviorSubject<boolean>(true);
  private $items = new BehaviorSubject<InOrder[]>([]);
  private $pageSizeOptions = new BehaviorSubject<number[]>(PAGE_SIZE_OPTIONS);
  private $currentPage = new BehaviorSubject<number>(1);
  private pageSize = PAGE_SIZE_OPTIONS[0];
  private pageCount = 0;
  private totalItems = 0;
  public currentPage = 1;

  constructor(private dataSource: InOrdersDataSourceService) { }

  getAll(filter: InOrdersFilterParams = {}, sortParams?: InOrdersSortParams) {
    const params: GetAllInOrdersParams = { ...filter, ...sortParams };
    return this.dataSource.getAllInOrders(params) as Promise<InOrdersApiResponse>;
  }

  async init(filter: InOrdersFilterParams = null, sortParams?: InOrdersSortParams) {
    return this.getPage(1, filter, sortParams);
  }

  async setPageSize(pageSize: number) {
    this.pageSize = pageSize;
    this.pageCount = Math.ceil(this.totalItems / this.pageSize);
    this.$pageSize.next(this.pageSize);
    await this.getPage(1);
  }

  async reload(filter: InOrdersFilterParams = null, sortParams?: InOrdersSortParams) {
    return this.getPage(this.currentPage, filter, sortParams);
  }

  async getNextPage(filter: InOrdersFilterParams = null, sortParams?: InOrdersSortParams) {
    if (this.currentPage < this.pageCount) {
      return this.getPage(this.currentPage + 1, filter, sortParams);
    }
    throw Error('???');
  }

  async getPreviousPage(filter: InOrdersFilterParams = null, sortParams?: InOrdersSortParams) {
    if (this.currentPage > 1) {
      return this.getPage(this.currentPage - 1, filter, sortParams);
    }
    throw Error('???');
  }

  async setPage(page: number, filter: InOrdersFilterParams = null, sortParams?: InOrdersSortParams) {
    this.currentPage = page;
    return this.getPage(this.currentPage, filter, sortParams);
  }

  private async getPage(
    page: number,
    filter: InOrdersFilterParams = {},
    sortParams?: InOrdersSortParams
  ) {
    this.currentPage = page;
    const params: GetAllInOrdersParams = {
      limit: this.pageSize,
      offset: (this.currentPage - 1) * this.pageSize
    };
    Object.assign(params, filter);
    Object.assign(params, sortParams);

    const response = await this.dataSource.getAllInOrders(params) as InOrdersApiResponse;
    this.totalItems = response.count;
    this.pageCount = Math.ceil(this.totalItems / this.pageSize);
    this.$items.next(response.values);
    this.$pageCount.next(this.pageCount);
    this.$totalItems.next(this.totalItems);
    this.$currentPage.next(this.currentPage);
    this.$atLastPage.next(this.pageCount === 0 || this.currentPage === this.pageCount);
    this.$atFirstPage.next(this.currentPage === 1);
  }

  get currentPage$(): Observable<number> {
    return this.$currentPage;
  }

  get atFirstPage$(): Observable<boolean> {
    return this.$atFirstPage;
  }

  get atLastPage$(): Observable<boolean> {
    return this.$atLastPage;
  }

  get totalItems$(): Observable<number> {
    return this.$totalItems;
  }

  get pageCount$(): Observable<number> {
    return this.$pageCount;
  }

  get items$(): Observable<InOrder[]> {
    return this.$items;
  }

  get pageSize$(): Observable<number> {
    return this.$pageSize;
  }

  get pageSizeOptions$(): Observable<number[]> {
    return this.$pageSizeOptions;
  }
}
